import { PROPERTY_GRID_SUBJECT_PROPERTY, isDefined, isUndefined } from '@aloft/toolkit-shared';
import { Block, Input, ProductColor, theme } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { Controller, ControllerRenderProps, FieldPath, useWatch } from 'react-hook-form';

import { useAppDispatch } from '../../../redux/hooks.ts';
import { PropertyGridFieldName, PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { PropertyGridFieldProps } from './types.ts';

export const SimpleEditableField = ({
  columnIndex,
  field,
  isArv = false,
  colSpan = 1,
  control,
  getValues,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();

  const { fieldName: rawFieldName, toDisplayValue, toEditValue, toValue } = field;

  const [currentParsedValue, setCurrentParsedValue] = useState<string | undefined>(undefined);

  const propertyId = getValues(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const arvFieldName = (rawFieldName as string).replace(
    /^editableData\./,
    'editableData.arv.',
  ) as PropertyGridFieldName;
  const fieldName = isSubject && isArv ? arvFieldName : rawFieldName;

  const propertyDataFieldName: FieldPath<PropertyGridForm> =
    `columns.${columnIndex}.propertyData.${fieldName}` as FieldPath<PropertyGridForm>;

  const value = useWatch({ control, name: propertyDataFieldName }) as string | undefined;

  const handleSetFieldValue = (onChange: ControllerRenderProps['onChange'], newValue?: string) => {
    const parsedNewValue = toValue ? toValue(newValue, propertyId) : newValue;
    setCurrentParsedValue(parsedNewValue);
    onChange(parsedNewValue);

    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
  };

  return (
    <Block gridColumn={`span ${colSpan}`} width="100%">
      <Controller
        name={propertyDataFieldName}
        control={control}
        render={({ field: { onChange } }) => {
          const displayValue = toDisplayValue ? toDisplayValue(value, propertyId) : value;

          let editValue = toEditValue ? toEditValue(value, propertyId) : displayValue;
          const currentEditValue = toEditValue
            ? toEditValue(currentParsedValue, propertyId)
            : currentParsedValue;

          if (isDefined(currentParsedValue) && currentEditValue !== editValue) {
            editValue = currentEditValue as string;
          }

          let title: string | undefined;
          let backgroundColor: ProductColor = 'surface1';
          if (isUndefined(displayValue)) {
            title = 'This field is empty. Please input a value.';
            backgroundColor = 'selected4';
          }

          return (
            <Input
              type="text"
              title={title}
              value={editValue}
              __dangerDoNotUseSx={{
                gridColumn: `span ${colSpan}`,
                backgroundColor,
                width: '100%',
                height: '32px',
                padding: '4px 24px 4px 8px',
                border: 'none',
                outline: 'none',
                borderRadius: '0',
                fontSize: '14px',
                _focusVisible: {
                  outline: '1.5px solid',
                  outlineOffset: '-1.5px',
                  outlineColor: theme.colors.selected2,
                },
              }}
              onFocus={() => handleSetFieldValue(onChange, editValue)}
              onBlur={() => handleSetFieldValue(onChange, editValue)}
              onChange={(v) => handleSetFieldValue(onChange, v)}
              onClear={() => handleSetFieldValue(onChange, undefined)}
              onKeyDown={(e) => {
                const isEnterKey = e.key === 'Enter' || e.key === 'NumpadEnter';
                const isEscapeKey = e.key === 'Escape';

                if (isEnterKey) handleSetFieldValue(onChange, editValue);
                if (isEscapeKey || isEnterKey) {
                  (document.activeElement as HTMLElement | null)?.blur();
                }
              }}
            />
          );
        }}
      />
    </Block>
  );
};
